import { useState } from 'react';
import { useMany, useTranslate, useUpdate } from '@refinedev/core';
import { DeleteButton, useTable } from '@refinedev/antd';
import { Button, Card, Dropdown, Space, Table } from 'antd';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import type {
  OrganizationMemberOut,
  OrganizationMemberUpdateIn,
  OrganizationOut,
  PublisherListOut,
  UserOut,
} from '@/api/generated/schemas';
import { OrganizationRole } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash } from '@/components/common/show/shared';
import { OrganizationRoleTag } from '@/components/tenancy/tags';
import { formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';
import { AddMemberModal } from './AddMemberModal';

interface OrganizationMembersTabProps {
  record: OrganizationOut;
  /** Members are mutated via the flat `tenancy.organization_members`
   * resource, but the header StatCell reads the copy embedded in the
   * organization record — the parent must refetch it on every change. */
  onMembersChanged: () => void;
}

export const OrganizationMembersTab = ({
  record,
  onMembersChanged,
}: OrganizationMembersTabProps) => {
  const t = useTranslate();
  const [addOpen, setAddOpen] = useState(false);
  const { mutate: updateMember } = useUpdate<OrganizationMemberOut>();

  const { tableProps, sorters, result } = useTable<OrganizationMemberOut>({
    resource: 'tenancy.organization_members',
    queryOptions: { enabled: record.id != null },
    filters: {
      permanent: [
        { field: 'organization_id', operator: 'eq', value: record.id },
      ],
    },
    sorters: {
      initial: [{ field: 'created_at', order: 'desc' }],
    },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  const members = result?.data ?? [];

  const userIds = [...new Set(members.map((m) => m.user_id))];
  const { result: usersResult } = useMany<UserOut>({
    resource: 'iam.users',
    ids: userIds,
    queryOptions: { enabled: userIds.length > 0 },
  });
  const userMap = new Map(
    (usersResult?.data ?? []).map((u: UserOut) => [u.id, u])
  );

  const publisherIds = [
    ...new Set(
      members
        .map((m) => m.publisher_id)
        .filter((id): id is number => id != null)
    ),
  ];
  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    queryOptions: { enabled: publisherIds.length > 0 },
  });
  const publisherMap = new Map(
    (publishersResult?.data ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  const changeRole = (member: OrganizationMemberOut, role: OrganizationRole) =>
    updateMember(
      {
        resource: 'tenancy.organization_members',
        id: member.id,
        values: { role } satisfies OrganizationMemberUpdateIn,
        meta: { method: 'patch' },
      },
      { onSuccess: onMembersChanged }
    );

  return (
    <>
      <Card
        title={`${t('tenancy.organizations.sections.members')} (${result?.total ?? record.members.length})`}
        extra={
          <Button icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            {t('tenancy.organizations.actions.add_member')}
          </Button>
        }
      >
        <Table<OrganizationMemberOut> {...tableProps} rowKey="id" size="small">
          <Table.Column<OrganizationMemberOut>
            dataIndex="user_id"
            title={t('tenancy.organizations.fields.user_id')}
            render={(userId: number) => {
              const user = userMap.get(userId);
              return (
                <ShowIconLink resource="iam.users" id={userId}>
                  {user?.email ?? user?.username ?? userId}
                </ShowIconLink>
              );
            }}
          />
          <Table.Column<OrganizationMemberOut>
            dataIndex="role"
            title={t('tenancy.organizations.fields.role')}
            render={(role: string, member) => (
              <Dropdown
                menu={{
                  items: Object.values(OrganizationRole).map((value) => ({
                    key: value,
                    label: t(`tenancy.organizations.role.${value}`),
                  })),
                  selectable: true,
                  selectedKeys: [role],
                  onClick: ({ key }) =>
                    changeRole(member, key as OrganizationRole),
                }}
              >
                <Space style={{ cursor: 'pointer' }}>
                  <OrganizationRoleTag role={role} />
                  <DownOutlined />
                </Space>
              </Dropdown>
            )}
          />
          <Table.Column<OrganizationMemberOut>
            dataIndex="publisher_id"
            title={t('tenancy.organizations.fields.publisher')}
            render={(publisherId: number | null) =>
              orDash(
                publisherId != null ? (
                  <ShowIconLink
                    resource="user_catalog.publishers"
                    id={publisherId}
                  >
                    {publisherMap.get(publisherId)?.ancestry_with_name ??
                      publisherId}
                  </ShowIconLink>
                ) : (
                  t('tenancy.organizations.fields.publisher_all')
                )
              )
            }
          />
          <Table.Column<OrganizationMemberOut>
            dataIndex="created_at"
            title={t('base.fields.created_at')}
            sorter
            sortOrder={getSortOrder(sorters, 'created_at')}
            render={(value: string) => formatDateTime(value)}
          />
          <Table.Column<OrganizationMemberOut>
            dataIndex="actions"
            title={t('actions.column')}
            render={(_, member) => (
              <DeleteButton
                hideText
                size="small"
                resource="tenancy.organization_members"
                recordItemId={member.id}
                onSuccess={onMembersChanged}
              />
            )}
          />
        </Table>
      </Card>

      <AddMemberModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        record={record}
        onMembersChanged={onMembersChanged}
      />
    </>
  );
};
